import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import highcharts3D from 'highcharts/highcharts-3d';
import HC_gantt from 'highcharts/modules/gantt';
import HC_timeLine from 'highcharts/modules/timeline';
import { constants } from '../../shared/constants';
import { CybersocXDRDashboardSearchCriteria } from '../../shared/model/searchCriteria';
import { CybersocxdrService } from '../../shared/services/xtendedsoc/cybersocxdr.service';
import HC_more from 'highcharts/highcharts-more';
import HC_accessibility from 'highcharts/modules/accessibility';
import HC_exporting from 'highcharts/modules/exporting';
import HC_organization from 'highcharts/modules/organization';
import HC_sankey from 'highcharts/modules/sankey';
import HC_solidGauge from 'highcharts/modules/solid-gauge.src';
import HC_sunburst from 'highcharts/modules/sunburst';
import HC_map from 'highcharts/modules/map';
import { IncidentScale } from 'src/app/shared/model/cybersocxdr/incidentScale';
import { CybersocxdrglobalfiltersService } from '../cybersocxdrglobalfilters.service';
import { MenuItem } from '../components/filtermenu/filtermenu.component';
import { Router } from '@angular/router';

HC_timeLine(Highcharts);
HC_exporting(Highcharts);
HC_gantt(Highcharts);
highcharts3D(Highcharts);
HC_accessibility(Highcharts);
HC_sankey(Highcharts);
HC_organization(Highcharts);
HC_more(Highcharts);
HC_solidGauge(Highcharts);
HC_sunburst(Highcharts);
HC_map(Highcharts);

@Component({
  selector: 'app-cybersocxdashboard',
  templateUrl: './cybersocxdrdashboard.component.html',
  styleUrls: ['./cybersocxdrdashboard.component.scss'],
})
export class CybersocxdrDashboardComponent implements OnInit {
  public page: 'highlevel' | 'backlog' | 'detailed' = 'highlevel';

  // data
  searched: boolean;
  createdFrom: string;
  createdTo: string;

  opentimeavgData: any;
  incidentsOverTimeRepartitionData: any;
  dashboardData: any;
  openDashboardData: any;
  incidentsOverTimedata: any;
  histogramOpenedIncidentsData: any;
  histogramClosedIncidentsData: any;
  histogramIncidentsRepartitionData: any;
  groupedIncidentsRepartitionData: any;
  incidentsRepartitionData: any[];
  topIncidentsByRepartitionData: any[];
  filteredRepartitionData: any[];
  categoryHeatMapData: any;

  bubbleHostnameData: any;
  pyramidIncidentData: IncidentScale;
  navigatorData: Array<{ category: string; percentage: number }>;

  // detailed view page state
  isGeographicDataAvailable = false; // determines if the worldmap is shown
  groupByLevel = '1';
  groupByTypes = [];

  subGroupbyField = 'severities';

  rootNode: any;
  refresh = false;

  // backlog section state
  hoveredRow: string = null;

  highcharts: typeof Highcharts = Highcharts;

  defaultFilterMenuItems: MenuItem[] = [
    {
      text: 'View Incidents',
      handler: (point) => {
        this.globalFilters.addFilter(point, true);
        this.router.navigate(['/xtended-soc/incidents']);
      },
    },
    {
      text: 'Filter In',
      icon: 'plus-circle',
      handler: (point) => {
        this.globalFilters.addFilter(point, true);
        this.search();
      },
    },
    {
      text: 'Filter Out',
      icon: 'minus-circle',
      handler: (point) => {
        this.globalFilters.addFilter(point, false);
        this.search();
      },
    },
  ];
  viewBacklogMenuItem: MenuItem = {
    text: 'View Backlog',
    handler: (point) => {
      this.globalFilters.addFilter(point, true);
      this.page = 'backlog';
      this.search();
    },
  };
  viewDetailsMenuItem: MenuItem = {
    text: 'View Details',
    handler: (point) => {
      this.globalFilters.addFilter(point, true);
      this.page = 'detailed';
      this.search();
    },
  };
  filterMenuItemsForHighLevelView: MenuItem[] = [this.viewDetailsMenuItem, ...this.defaultFilterMenuItems];
  filterMenuItemsForBacklogView: MenuItem[] = [
    this.viewDetailsMenuItem,
    this.viewBacklogMenuItem,
    ...this.defaultFilterMenuItems,
  ];
  filterMenuItemsForDetailedView: MenuItem[] = [this.viewBacklogMenuItem, ...this.defaultFilterMenuItems];
  constructor(
    private cybersocxdrService: CybersocxdrService,
    private translate: TranslateService,
    private globalFilters: CybersocxdrglobalfiltersService,
    private router: Router
  ) {}

  /** Trigger a search for pretty much every graph data */
  async search() {
    this.globalFilters.updateSearchCriteria();
    const searchCriteria = this.globalFilters.searchCriteria;
    this.createdTo = searchCriteria.createdTo;
    this.createdFrom = searchCriteria.createdFrom;
    this.searched = true;
    searchCriteria.limit = constants.recordsPerPage.microsocticket;

    // Priorize the query to be displayed first
    let searchQueries = [this.searchHighLevelView, this.searchBacklogView, this.searchDetailedView];
    const prioritizedSearch = {
      backlog: this.searchBacklogView,
      highlevel: this.searchHighLevelView,
      detailed: this.searchDetailedView,
    }[this.page];

    searchQueries = searchQueries.filter((searchQuery) => searchQuery !== prioritizedSearch);
    searchQueries.unshift(prioritizedSearch);
    for (const searchQuery of searchQueries) {
      await searchQuery.call(this, searchCriteria);
    }
  }

  /** Update all the fields used to display the high level view tab */
  async searchHighLevelView(criteria: CybersocXDRDashboardSearchCriteria) {
    [
      this.dashboardData,
      this.incidentsOverTimedata,
      this.categoryHeatMapData,
      this.bubbleHostnameData,
      this.pyramidIncidentData,
    ] = await Promise.all([
      this.cybersocxdrService.getDashboardData(criteria),
      this.cybersocxdrService.getIncidentsOverTime({ ...criteria, groupbyField: 'severities' }),
      this.cybersocxdrService.getCategoryHeatMap(criteria),
      this.cybersocxdrService.getVolumeBubbles(criteria),
      this.cybersocxdrService.getIncidentCaseScale(criteria),
    ]);
    this.getSecurityNavigator(criteria);
  }

  /** Update all fields related to the backlog view tab */
  async searchBacklogView(criteria: CybersocXDRDashboardSearchCriteria) {
    [
      this.opentimeavgData,
      this.openDashboardData,
      this.histogramOpenedIncidentsData,
      this.histogramClosedIncidentsData,
    ] = await Promise.all([
      this.cybersocxdrService.getOpenTimeAVGOvertime(criteria),
      this.cybersocxdrService.getDashboardData({ ...criteria, status: ['New', 'Investigation', 'Remediation'] }),
      this.cybersocxdrService.getHistogramIncidents({
        ...criteria,
        status: ['New', 'Investigation', 'Remediation'],
        groupbyField: 'waiting_for',
      }),
      // This one uses incident closed, without caring of incidents creation date.
      this.cybersocxdrService.getHistogramIncidents({
        ...criteria,
        status: ['Closed'],
        groupbyField: 'closure_verdict',
        closedFrom: criteria.createdFrom,
        closedTo: criteria.createdTo,
        createdFrom: undefined,
        createdTo: undefined,
      }),
    ]);
  }

  /** Update all fields related to the detailed view tab */
  async searchDetailedView(criteria: CybersocXDRDashboardSearchCriteria) {
    [
      this.incidentsRepartitionData,
      this.topIncidentsByRepartitionData,
      this.incidentsOverTimeRepartitionData,
      this.histogramIncidentsRepartitionData,
      this.groupedIncidentsRepartitionData,
    ] = await Promise.all([
      this.cybersocxdrService.getIncidentsRepartitions(criteria),
      this.cybersocxdrService.getTopIncidentsByRepartitions(
        { ...criteria, status: ['New', 'Investigation', 'Remediation'] },
        this.groupByLevel
      ),
      this.cybersocxdrService.getIncidentsOverTime({
        ...criteria,
        repartitionGroupbyLevel: parseInt(this.groupByLevel, 10),
      }),
      this.cybersocxdrService.getHistogramIncidents({
        ...criteria,
        status: ['New', 'Investigation', 'Remediation'],
        repartitionGroupbyLevel: parseInt(this.groupByLevel, 10),
      }),
      this.cybersocxdrService.getLeveledGroupedIncidents({
        ...criteria,
        subGroupbyField: this.subGroupbyField,
        repartitionGroupbyLevel: parseInt(this.groupByLevel, 10),
      }),
    ]);
  }

  async ngOnInit() {
    this.groupByTypes = await this.cybersocxdrService.getGroupByFilterTypes();
    this.isGeographicDataAvailable = this.groupByTypes.includes('country');
    await this.globalFilters.init();
  }

  getSecurityNavigator(searchCriteria: CybersocXDRDashboardSearchCriteria) {
    this.cybersocxdrService.getTruePositiveDetails(searchCriteria).then((res) => {
      this.navigatorData = res;
    });
  }

  async updateLeveledGroupBy(groupby: string) {
    this.subGroupbyField = groupby;
    this.groupedIncidentsRepartitionData = await this.cybersocxdrService.getLeveledGroupedIncidents({
      ...this.globalFilters.searchCriteria,
      subGroupbyField: this.subGroupbyField,
      repartitionGroupbyLevel: parseInt(this.groupByLevel, 10),
    });
  }

  updateHovered(hoveredRow: IteratorResult<string>) {
    if (hoveredRow.done) {
      this.hoveredRow = null;
    } else {
      this.hoveredRow = hoveredRow.value;
    }
  }
}
